"use client";

import { useEffect, useRef, useState } from "react";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, IS_AONSAT_ENABLED } from "@/context/utils";
import { aonsatAssociatedPrograms } from "../../../Forms/ConnectModalFormIONSAT/utils";
import type {
  ContentBodyInput,
  ContentBodyViewModel,
} from "../model/contentBody.types";

export function useContentBodyController(
  props: ContentBodyInput,
): ContentBodyViewModel {
  const { data, heading, updatedAt, associatedPrograms } = props;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const modalDispatch = useModalDispatch();
  const [isTocOpen, setIsTocOpen] = useState(false);
  const [activeTocIndex, setActiveTocIndex] = useState<number | null>(null);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) {
      return;
    }

    const onArticleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a[href*='?modalId=']");
      const requestButton = target.closest("#requestCallBtn");
      const enrollButton = target.closest("#enrollBtn");
      const isEnrollButton = Boolean(enrollButton);

      if (!anchor && !requestButton && !enrollButton) {
        return;
      }

      event.preventDefault();
      const ctaText = target.innerText || "NA";
      const sectionName = isEnrollButton ? "footer" : anchor ? "hero_banner" : "middle";
      const headerTitle = isEnrollButton
        ? "Are you ready? To Take The Next Step In Your Career?"
        : anchor
          ? "NA"
          : "Get Complete Details From Expert";

      pushToDataLayer({
        event: "lead_initiate",
        cta_text: isEnrollButton || requestButton ? ctaText : "NA",
        section_name: sectionName,
        header_title: headerTitle,
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href || "NA",
      });

      if (IS_AONSAT_ENABLED && !isEnrollButton) {
        modalDispatch({
          type: IModalActions.OpenModal,
          data: {
            modalType: ModalTypes["ConnectModalAONSAT"],
            isModalOpen: true,
            modalData: {
              heading: "Register for Amity Online NSAT 2026",
              associatedPrograms: aonsatAssociatedPrograms,
              submitBtnText: "START TEST",
              rfiCase: "REDIRECT_TO_APPLICATION_JOURNEY",
            },
          },
        });
        return;
      }

      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes[isEnrollButton ? "ConnectModal" : IS_AONSAT_ENABLED ? "ConnectModal" : "ConnectModalV2"],
          isModalOpen: true,
          ...(enrollButton
            ? {}
            : {
                modalData: {
                  associatedPrograms,
                  heading: "Join India's No. 1 Online University",
                  submitBtnText: "Apply Now",
                  isLandingPage: true,
                  rfiCase: "REDIRECT_APPLICATION_JOURNEY",
                },
              }),
        },
      });
    };

    container.addEventListener("click", onArticleClick);
    return () => container.removeEventListener("click", onArticleClick);
  }, [associatedPrograms, modalDispatch]);

  return {
    processedHtml: props.processedHtml,
    tocItems: props.tocItems,
    heading,
    updatedAt,
    author: data.author,
    associatedPrograms,
    contentRef,
    isTocOpen,
    activeTocIndex,
    toggleToc: () => setIsTocOpen((open) => !open),
    selectTocItem: (item, index) => {
      setActiveTocIndex(index);
      window.history.pushState(null, "", `#${item.anchorId}`);
      document
        .getElementById(item.anchorId)
        ?.scrollIntoView({ behavior: "smooth" });
    },
  };
}
